import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, IconButton, Typography, Slider, Stack, useTheme } from '@mui/material';
import {
    VolumeUp,
    VolumeOff,
    Fullscreen,
    FullscreenExit,
    Pause,
    PlayArrow,
} from '@mui/icons-material';

interface VideoPlayerProps {
    src: string;
    title?: string;
    poster?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, title, poster }) => {
    const theme = useTheme();
    const isMobile = useMemo(() => {
        const ua = navigator.userAgent;
        if (/iPhone|iPod|Android/i.test(ua)) return true;
        // iPadOS 13+ reports as Mac — detect via touch support
        if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
        return false;
    }, []);
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const volumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const touchStartRef = useRef<{ time: number; x: number; y: number } | null>(null);
    // Add smooth progress interval
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const setVideoData = () => {
            setDuration(video.duration);
            setCurrentTime(video.currentTime);
        };

        const setVideoTime = () => setCurrentTime(video.currentTime);

        const handlePlay = () => {
            setIsPlaying(true);
            // Start smooth progress updates (10 times per second for YouTube-like smoothness)
            progressIntervalRef.current = setInterval(() => {
                if (video && !video.paused && !video.ended) {
                    setCurrentTime(video.currentTime);
                }
            }, 100); // 100ms = 10 updates per second
        };

        const handlePause = () => {
            setIsPlaying(false);
            // Clear smooth updates when paused
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
        };

        const handleEnded = () => {
            setIsPlaying(false);
            // Clear smooth updates when ended
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
        };

        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement || (document as any).webkitFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);

            // Handle escape key to exit fullscreen
            if (!isCurrentlyFullscreen && isFullscreen) {
                setShowControls(true);
            }
        };

        // iOS fires these on the video element when using webkitEnterFullscreen
        const handleBeginFullscreen = () => setIsFullscreen(true);
        const handleEndFullscreen = () => setIsFullscreen(false);

        video.addEventListener('loadeddata', setVideoData);
        video.addEventListener('timeupdate', setVideoTime);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        video.addEventListener('webkitbeginfullscreen', handleBeginFullscreen);
        video.addEventListener('webkitendfullscreen', handleEndFullscreen);

        return () => {
            video.removeEventListener('loadeddata', setVideoData);
            video.removeEventListener('timeupdate', setVideoTime);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener(
                'webkitfullscreenchange',
                handleFullscreenChange,
            );
            video.removeEventListener('webkitbeginfullscreen', handleBeginFullscreen);
            video.removeEventListener('webkitendfullscreen', handleEndFullscreen);

            // Cleanup timeouts and interval
            if (volumeTimeoutRef.current) {
                clearTimeout(volumeTimeoutRef.current);
            }
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, [isFullscreen]);

    const togglePlayPause = () => {
        const video = videoRef.current;
        if (!video) return;

        if (isPlaying) {
            video.pause();
        } else {
            video.play().catch((error) => {
                console.error('Error playing video:', error);
                setIsPlaying(false);
            });
        }
    };

    const handleProgressChange = (event: Event, newValue: number | number[]) => {
        const video = videoRef.current;
        if (!video) return;

        const value = newValue as number;
        video.currentTime = value;
        setCurrentTime(value);
    };

    const handleVolumeChange = (event: Event, newValue: number | number[]) => {
        const video = videoRef.current;
        if (!video) return;

        const value = newValue as number;
        video.volume = value;
        setVolume(value);
        setIsMuted(value === 0);
    };

    const toggleMute = () => {
        const video = videoRef.current;
        if (!video) return;

        if (isMuted) {
            const restoreVolume = volume > 0 ? volume : 0.5;
            video.volume = restoreVolume;
            setVolume(restoreVolume);
            setIsMuted(false);
        } else {
            video.volume = 0;
            setIsMuted(true);
        }
    };

    const toggleFullscreen = () => {
        const container = containerRef.current;
        const video = videoRef.current;
        if (!container) return;

        if (!isFullscreen) {
            // Try standard Fullscreen API on the container first
            if (container.requestFullscreen) {
                container.requestFullscreen().catch((error) => {
                    console.error('Error entering fullscreen:', error);
                });
            } else if ((container as any).webkitRequestFullscreen) {
                (container as any).webkitRequestFullscreen();
            } else if (video && (video as any).webkitEnterFullscreen) {
                // iOS Safari: only supports fullscreen on <video> elements
                (video as any).webkitEnterFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch((error) => {
                    console.error('Error exiting fullscreen:', error);
                });
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            }
        }
    };

    const formatTime = (time: number) => {
        if (isNaN(time)) return '0:00';

        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleVolumeMouseEnter = () => {
        if (volumeTimeoutRef.current) {
            clearTimeout(volumeTimeoutRef.current);
        }
        setShowVolumeSlider(true);
    };

    const handleVolumeMouseLeave = () => {
        volumeTimeoutRef.current = setTimeout(() => {
            setShowVolumeSlider(false);
        }, 300);
    };

    const handleMouseMove = () => {
        setShowControls(true);
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        controlsTimeoutRef.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
            }
        }, 3000);
    };

    const handleVideoClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        togglePlayPause();
    };

    const handleVideoTouchStart = (event: React.TouchEvent) => {
        // Record touch start position — don't preventDefault so scrolling still works
        const touch = event.touches[0];
        touchStartRef.current = { time: Date.now(), x: touch.clientX, y: touch.clientY };
    };

    const handleVideoTouchEnd = (event: React.TouchEvent) => {
        if (!touchStartRef.current) return;

        const touch = event.changedTouches[0];
        const dx = Math.abs(touch.clientX - touchStartRef.current.x);
        const dy = Math.abs(touch.clientY - touchStartRef.current.y);
        const dt = Date.now() - touchStartRef.current.time;
        touchStartRef.current = null;

        // Only toggle play/pause on a quick tap (not a scroll/swipe)
        if (dx < 10 && dy < 10 && dt < 300) {
            event.preventDefault();
            togglePlayPause();
        }
    };

    const handleContainerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && isFullscreen) {
            event.preventDefault();
            toggleFullscreen();
        }
        if (event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            togglePlayPause();
        }
    };

    return (
        <Box
            ref={containerRef}
            tabIndex={0} // Make the container focusable for keyboard events
            onKeyDown={handleContainerKeyDown}
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                borderRadius: isFullscreen ? '0px' : '16px',
                overflow: 'hidden',
                backgroundColor: 'black',
                cursor: showControls ? 'default' : 'none',
                outline: 'none !important', // Remove default focus outline
                boxShadow: 'none !important', // Remove any focus box shadow
                '&:focus': {
                    outline: 'none !important',
                    boxShadow: 'none !important',
                },
                '&:focus-visible': {
                    outline: 'none !important',
                    boxShadow: 'none !important',
                },
                // Fullscreen styles
                ...(isFullscreen && {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }),
                '&:hover': {
                    '& .video-controls': {
                        opacity: 1,
                    },
                },
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => {
                if (controlsTimeoutRef.current) {
                    clearTimeout(controlsTimeoutRef.current);
                }
                setShowControls(true);
            }}
        >
            <video
                ref={videoRef}
                src={src}
                poster={poster}
                onClick={handleVideoClick}
                onTouchStart={handleVideoTouchStart}
                onTouchEnd={handleVideoTouchEnd}
                onContextMenu={(e) => e.preventDefault()}
                playsInline
                webkit-playsinline='true'
                style={{
                    width: isFullscreen ? 'auto' : '100%',
                    height: isFullscreen ? '100vh' : 'auto',
                    maxWidth: isFullscreen ? '100vw' : '100%',
                    maxHeight: isFullscreen ? '100vh' : 'none',
                    display: 'block',
                    cursor: 'pointer',
                    objectFit: 'contain',
                    WebkitUserSelect: 'none',
                    userSelect: 'none' as const,
                }}
            />

            {/* Play Button Overlay (when paused) - Fixed z-index issue */}
            {!isPlaying && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 3, // Higher than controls
                        pointerEvents: 'auto',
                    }}
                >
                    <IconButton
                        onClick={togglePlayPause}
                        sx={{
                            backgroundColor: `${theme.palette.primary.main}90`,
                            color: 'white',
                            width: 80,
                            height: 80,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                                transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        }}
                    >
                        <PlayArrow sx={{ fontSize: 40 }} />
                    </IconButton>
                </Box>
            )}

            {/* Video Controls Overlay */}
            <Box
                className='video-controls'
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    padding: '20px 16px 16px',
                    opacity: showControls ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                    zIndex: 2,
                    // Fix click blocking when controls are hidden
                    pointerEvents: showControls ? 'auto' : 'none',
                }}
            >
                {/* Title */}
                {title && (
                    <Typography
                        variant='body2'
                        sx={{
                            color: 'white',
                            fontWeight: 600,
                            marginBottom: '12px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                        }}
                    >
                        {title}
                    </Typography>
                )}

                {/* Progress Bar */}
                <Box sx={{ marginBottom: '12px' }}>
                    <Slider
                        size='small'
                        value={currentTime}
                        max={duration}
                        onChange={handleProgressChange}
                        sx={{
                            color: theme.palette.primary.main,
                            height: 6,
                            '& .MuiSlider-track': {
                                border: 'none',
                                height: 6,
                                borderRadius: 3,
                            },
                            '& .MuiSlider-rail': {
                                opacity: 0.4,
                                backgroundColor: 'rgba(255,255,255,0.3)',
                                height: 6,
                                borderRadius: 3,
                            },
                            '& .MuiSlider-thumb': {
                                height: 18,
                                width: 18,
                                backgroundColor: theme.palette.primary.main,
                                border: '2px solid white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                                    boxShadow: `0 0 0 8px ${theme.palette.primary.main}30`,
                                },
                            },
                        }}
                    />
                </Box>

                {/* Controls */}
                <Stack direction='row' alignItems='center' spacing={1}>
                    {/* Play/Pause Button */}
                    <IconButton
                        onClick={togglePlayPause}
                        sx={{
                            color: 'white',
                            backgroundColor: `${theme.palette.primary.main}90`,
                            width: 48,
                            height: 48,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                        }}
                    >
                        {isPlaying ? <Pause /> : <PlayArrow />}
                    </IconButton>

                    {/* Time Display */}
                    <Typography
                        variant='caption'
                        sx={{
                            color: 'white',
                            fontSize: '0.875rem',
                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                            minWidth: '80px',
                        }}
                    >
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>

                    {/* Spacer */}
                    <Box sx={{ flex: 1 }} />

                    {/* Volume Control - hidden on mobile where OS controls volume */}
                    {!isMobile && (
                        <Box
                            sx={{ position: 'relative' }}
                            onMouseEnter={handleVolumeMouseEnter}
                            onMouseLeave={handleVolumeMouseLeave}
                        >
                            <IconButton
                                onClick={toggleMute}
                                size='small'
                                sx={{
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: `${theme.palette.primary.main}20`,
                                    },
                                }}
                            >
                                {isMuted ? <VolumeOff /> : <VolumeUp />}
                            </IconButton>

                            {showVolumeSlider && (
                                <Box
                                    onMouseEnter={handleVolumeMouseEnter}
                                    onMouseLeave={handleVolumeMouseLeave}
                                    sx={{
                                        position: 'absolute',
                                        bottom: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: theme.palette.background.paper,
                                        borderRadius: '8px',
                                        padding: '8px 4px',
                                        boxShadow: theme.shadows[8],
                                        border: `1px solid ${theme.palette.divider}`,
                                        marginBottom: '8px',
                                        zIndex: 10,
                                    }}
                                >
                                    <Slider
                                        orientation='vertical'
                                        value={isMuted ? 0 : volume}
                                        min={0}
                                        max={1}
                                        step={0.01}
                                        onChange={handleVolumeChange}
                                        sx={{
                                            color: theme.palette.primary.main,
                                            height: 80,
                                            '& .MuiSlider-thumb': {
                                                width: 16,
                                                height: 16,
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Fullscreen Button */}
                    <IconButton
                        onClick={toggleFullscreen}
                        size='small'
                        sx={{
                            color: 'white',
                            '&:hover': {
                                backgroundColor: `${theme.palette.primary.main}20`,
                            },
                        }}
                    >
                        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                    </IconButton>
                </Stack>
            </Box>
        </Box>
    );
};

export default VideoPlayer;
